import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardAPI, authAPI, userSettingsAPI, clubAPI, bookieAPI } from '../services/api';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';
import WallFeed from './WallFeed';
import ClubLotto from './ClubLotto';
import ClubCrypto from './ClubCrypto';
import Footer from './Footer';
import './ClubDashboard.css';

function ClubDashboard() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { theme, cycleTheme } = useTheme();
  const [clubs, setClubs] = useState([]);
  const [showClubSelector, setShowClubSelector] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard' or 'settings'
  const [settingsData, setSettingsData] = useState(null);
  const [settingsFormData, setSettingsFormData] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [eligibleCaptains, setEligibleCaptains] = useState([]);
  const [startingClub, setStartingClub] = useState(false);
  const [settingsTab, setSettingsTab] = useState('settings'); // 'settings', 'members', 'statement'
  const [transactionTab, setTransactionTab] = useState('kitty'); // 'kitty' or 'betting'
  const [showBetModal, setShowBetModal] = useState(false);
  const [betLoading, setBetLoading] = useState(false);
  const [bookieBalances, setBookieBalances] = useState(null);
  const [dashboardMode, setDashboardMode] = useState('betting'); // 'betting' or 'lotto'
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [reloadingBetting, setReloadingBetting] = useState(false);
  const [reloadBettingResult, setReloadBettingResult] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    loadDashboard();
    // Fetch notification count
    api.get('/api/notifications/count').then(res => {
      setNotificationCount(res.data?.count || 0);
    }).catch(() => {});
    // Fetch user's clubs for switcher
    api.get('/api/user/clubs').then(res => {
      setClubs(res.data?.clubs || res.data || []);
    }).catch(() => {});
    // Fetch notifications
    api.get('/api/notifications?limit=20').then(res => {
      setNotifications(res.data?.notifications || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  // Countdown timer
  useEffect(() => {
    if (!dashboardData?.puntingSchedule?.periodEnd) return;

    const updateCountdown = () => {
      const end = new Date(dashboardData.puntingSchedule.periodEnd);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [dashboardData?.puntingSchedule?.periodEnd]);

  // Theme is now managed by ThemeContext

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClubSelector && !event.target.closest('.club-selector-wrapper')) {
        setShowClubSelector(false);
      }
      if (showNotifications && !event.target.closest('.notification-bell')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showClubSelector]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const userData = await authAPI.getCurrentUser();
      setUser(userData.user);

      // Set this club as the active club
      await userSettingsAPI.setActiveClub(parseInt(clubId));

      const data = await dashboardAPI.getClubDashboard(clubId);
      setDashboardData(data);

      // Fetch all user's clubs for the club selector
      const clubData = await clubAPI.listClubs();
      setClubs(clubData.clubs || []);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');

      if (err.response?.status === 401) {
        setTimeout(() => navigate('/login'), 2000);
      } else if (err.response?.status === 403) {
        // Not a member — clear active club and go to main dashboard
        try { await userSettingsAPI.setActiveClub(0); } catch (e) { /* */ }
        setTimeout(() => navigate('/dashboard?showAll=1'), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await api.post('/api/notifications/read', { ids: [id] });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setNotificationCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/read', { ids: [] });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setNotificationCount(0);
    } catch (e) {}
  };

  // toggleTheme replaced by cycleTheme from ThemeContext

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatCurrencyShort = (cents) => {
    return `$${Math.floor(cents / 100)}`;
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    }
  };

  const renderTransactionsWithDateHeaders = (transactions) => {
    let lastDate = null;
    const rows = [];

    transactions.forEach((txn, idx) => {
      const txnDate = txn.date ? new Date(txn.date).toDateString() : null;

      // Add date header row if this is a new date
      if (txnDate && txnDate !== lastDate) {
        rows.push(
          <tr key={`date-${txnDate}-${idx}`} className="transaction-date-header">
            <td colSpan="4" className="date-header-cell">
              {formatDateHeader(txn.date)}
            </td>
          </tr>
        );
        lastDate = txnDate;
      }

      // Add the transaction row
      rows.push(
        <tr key={txn.id || idx} className={`transaction-row ${txn.status}`}>
          <td className="transaction-time">
            {txn.date ? new Date(txn.date).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-'}
            {txn.status === 'failed' && (
              <div className="transaction-failure-info">
                <span className="transaction-status failed">FAILED</span>
              </div>
            )}
          </td>
          <td className="transaction-desc">
            <div className="transaction-description">{txn.description || 'Transaction'}</div>
            <div className="transaction-amount-mobile">${(Math.abs(txn.amount || 0) / 100).toFixed(2)}</div>
            {txn.status === 'failed' && txn.failureReason && (
              <div className="transaction-failure-reason">{txn.failureReason}</div>
            )}
            {txn.status === 'refunded' && <span className="transaction-status refunded">REFUNDED</span>}
          </td>
          <td className="transaction-amount text-right">
            <span className={`${txn.amount >= 0 ? 'positive' : 'negative'}${txn.status === 'failed' ? ' failed-amount' : ''}`}>
              {txn.amount >= 0 ? '' : '-'}${(Math.abs(txn.amount || 0) / 100).toFixed(2)}
            </span>
          </td>
          <td className="transaction-balance text-right">
            ${((txn.balance || 0) / 100).toFixed(2)}
          </td>
        </tr>
      );
    });

    return rows;
  };

  const handleSwitchClub = (newClubId) => {
    setShowClubSelector(false);
    if (newClubId !== parseInt(clubId)) {
      navigate(`/dashboard/${newClubId}`);
    }
  };

  const loadSettingsData = async () => {
    try {
      const data = await clubAPI.getClubEditData(clubId);
      if (data.success) {
        setSettingsData(data.club);
        setSettingsFormData(data.club);
        setEligibleCaptains(data.eligibleCaptains);
      } else {
        setSettingsError(data.error || 'Failed to load settings');
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to load settings');
    }
  };

  const handleManageClubClick = () => {
    if (activeView === 'settings') {
      setActiveView('dashboard');
    } else {
      setActiveView('settings');
      if (!settingsData) {
        loadSettingsData();
      }
    }
  };

  const handleSettingsInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsSuccess('');

    try {
      const data = await clubAPI.updateClub(clubId, settingsFormData);
      if (data.success) {
        setSettingsSuccess('Club settings saved successfully');
        setTimeout(() => setSettingsSuccess(''), 3000);
        // Reload dashboard to reflect changes
        loadDashboard();
      } else {
        setSettingsError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handlePauseToggle = async () => {
    try {
      const data = await clubAPI.togglePauseClub(clubId);
      if (data.success) {
        setSettingsFormData(prev => ({ ...prev, isPaused: data.isPaused }));
        // Also update dashboard data so stats bar updates immediately
        setDashboardData(prev => ({
          ...prev,
          club: { ...prev.club, isPaused: data.isPaused }
        }));
        setSettingsSuccess(data.message);
        setTimeout(() => setSettingsSuccess(''), 3000);
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to toggle pause');
    }
  };

  const handleStartClub = async () => {
    if (startingClub) return;

    setStartingClub(true);
    try {
      const data = await clubAPI.startClub(clubId);
      if (data.success) {
        // Reload dashboard to reflect changes
        await loadDashboard();
        setSettingsSuccess(data.message || 'Club has been started!');
        setTimeout(() => setSettingsSuccess(''), 3000);
      } else {
        setSettingsError(data.error || 'Failed to start club');
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to start club');
    } finally {
      setStartingClub(false);
    }
  };

  const handleMemberStatusChange = async (userId, newStatus) => {
    try {
      setSettingsError('');
      let data;
      if (newStatus === 'suspended') {
        data = await clubAPI.suspendMember(clubId, userId);
      } else if (newStatus === 'active') {
        data = await clubAPI.activateMember(clubId, userId);
      }
      if (data?.success) {
        setSettingsSuccess(data.message);
        setTimeout(() => setSettingsSuccess(''), 3000);
        await loadDashboard();
      } else {
        setSettingsError(data?.error || 'Failed to update member status');
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to update member status');
    }
  };

  const handleEjectMember = async (userId, memberName) => {
    if (!window.confirm(`Are you sure you want to eject ${memberName} from the club? This action cannot be undone.`)) {
      return;
    }
    try {
      setSettingsError('');
      const data = await clubAPI.ejectMember(clubId, userId);
      if (data.success) {
        setSettingsSuccess(data.message);
        setTimeout(() => setSettingsSuccess(''), 3000);
        await loadDashboard();
      } else {
        setSettingsError(data.error || 'Failed to eject member');
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to eject member');
    }
  };

  const handleMemberBettingChange = async (userId, shouldBet) => {
    try {
      setSettingsError('');
      const data = await clubAPI.setMemberBetting(clubId, userId, shouldBet);
      if (data.success) {
        setSettingsSuccess(data.message);
        setTimeout(() => setSettingsSuccess(''), 3000);
        await loadDashboard();
      } else {
        setSettingsError(data.error || 'Failed to update betting status');
      }
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to update betting status');
    }
  };

  const openBetModal = async () => {
    setShowBetModal(true);
    setBalanceLoading(true);
    setBookieBalances(null);
    try {
      const data = await bookieAPI.getBookieBalance(clubId);
      if (data.success) {
        setBookieBalances(data.balances);
      }
    } catch (err) {
      // Balances are optional — don't block the modal
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleBetNow = async (walletType) => {
    setBetLoading(true);
    try {
      const data = await bookieAPI.getBetLaunchUrl(clubId, walletType);
      if (data.success && data.url) {
        window.open(data.url, '_blank');
        setShowBetModal(false);
      } else {
        setError(data.error || 'Failed to get betting URL');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to launch betting');
    } finally {
      setBetLoading(false);
    }
  };

  const settingsOptions = {
    daysOfWeek: [
      { value: 0, label: 'Sunday' },
      { value: 1, label: 'Monday' },
      { value: 2, label: 'Tuesday' },
      { value: 3, label: 'Wednesday' },
      { value: 4, label: 'Thursday' },
      { value: 5, label: 'Friday' },
      { value: 6, label: 'Saturday' },
    ],
    frequencyOptions: [
      { value: 'weekly', label: 'Weekly' },
      { value: 'daily', label: 'Daily' },
    ],
    amountTypeOptions: [
      { value: 'percent', label: 'Percentage of kitty' },
      { value: 'dollars', label: 'Fixed dollar amount' },
    ],
    contributionFrequencyOptions: [
      { value: 'weekly', label: 'Weekly' },
      { value: 'fortnightly', label: 'Fortnightly' },
      { value: 'monthly', label: 'Monthly' },
    ],
    membershipStateOptions: [
      { value: 1, label: 'Public - Anyone with link can join' },
      { value: 2, label: 'Captain Invite Only' },
      { value: 3, label: 'Member Invite Only' },
      { value: 4, label: 'Closed - No new members' },
    ],
    leaderboardTypeOptions: [
      { value: 'all_members', label: 'All Members' },
      { value: 'punters_only', label: 'Punters Only' },
    ],
  };

  if (loading) {
    return (
      <div className="club-dashboard-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <img src="/images/beer-loading.gif" alt="Loading..." style={{ width: '80px', height: 'auto', marginBottom: '16px' }} />
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Loading club dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="club-dashboard-wrapper">
        <div className="error-container">
          <h2>Error</h2>
          <p className="error-message">{error}</p>
          <button className="btn-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { club, stats, members, memberCount, accounts, puntingSchedule } = dashboardData;
  const totalBalance = (accounts?.betting?.balance || 0) + (accounts?.kitty?.balance || 0);
  const hasStarted = club.startDate && new Date(club.startDate) <= new Date();

  return (
    <div>
      {/* Header */}
      <header className="header">
        <div className="header-top">
          <div className="header-wrapper">
            <a href="/dashboard" className="logo">
              <img src="/logo.png" alt="PuntClub" />
            </a>
            <div className="header-actions">
              <button className="theme-toggle" onClick={cycleTheme} title={`Theme: ${theme}`}>
                <i className={`fas fa-${theme === 'dark' ? 'sun' : theme === 'light' ? 'moon' : 'palette'}`}></i>
              </button>
              {hasStarted ? (
                <button className="btn-primary" onClick={openBetModal}>BET NOW</button>
              ) : (
                <button className="btn-primary btn-start" onClick={handleStartClub} disabled={startingClub}>
                  {startingClub ? 'STARTING...' : 'START NOW'}
                </button>
              )}
              <span className="user-name">{user?.firstname} {user?.surname}</span>
              <div className="notification-bell" onClick={() => setShowNotifications(!showNotifications)} style={{ cursor: 'pointer', position: 'relative', marginRight: 8 }}>
                <i className="fas fa-bell" style={{ fontSize: 18, color: '#aaa' }}></i>
                {notificationCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -6, right: -8, background: '#ef4444', color: '#fff',
                    borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>{notificationCount > 99 ? '99+' : notificationCount}</span>
                )}
                {showNotifications && (
                  <div className="notification-dropdown" onClick={(e) => e.stopPropagation()}>
                    <div className="notification-dropdown-header">
                      <span>Notifications</span>
                      {notifications.length > 0 && (
                        <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#fbbf24', fontSize: 12, cursor: 'pointer' }}>Mark all read</button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="notification-empty">No notifications</div>
                    ) : (
                      notifications.map(n => (
                        <div key={n.id} onClick={() => { if (!n.isRead) markNotificationRead(n.id); }}
                          className={`notification-item ${!n.isRead ? 'unread' : ''}`}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <span style={{
                              color: n.title?.includes('WON') ? '#10b981' : n.title?.includes('LOST') ? '#ef4444' : '#fbbf24',
                              fontSize: 11, fontWeight: 700, textTransform: 'uppercase'
                            }}>{n.title || n.type}</span>
                            <span style={{ color: '#555', fontSize: 10 }}>
                              {n.created ? new Date(n.created).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }) : ''}
                            </span>
                          </div>
                          <div className={`notification-message ${n.isRead ? 'read' : ''}`}
                            style={{ color: n.isRead ? '#777' : '#ccc' }}>
                            {(n.message || '').replace(/<[^>]*>/g, '').substring(0, 120)}{(n.message || '').length > 120 ? '...' : ''}
                          </div>
                        </div>
                      ))
                    )}
                    <div className="notification-dropdown-footer">
                      <button onClick={() => { setShowNotifications(false); navigate('/settings/notifications'); }}
                        style={{ background: 'none', border: 'none', color: '#fbbf24', fontSize: 12, cursor: 'pointer' }}>
                        Notification Settings
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="menu-icon" onClick={() => setShowMenu(!showMenu)} style={{ cursor: 'pointer', position: 'relative' }}>
                <i className="fas fa-bars"></i>
                {showMenu && (
                  <div className="club-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    {clubs.length > 1 && (
                      <>
                        <div className="dropdown-section-title">Switch Club</div>
                        {clubs.filter(c => String(c.clubId) !== String(clubId)).map(c => (
                          <button key={c.clubId} onClick={() => { setShowMenu(false); navigate(`/dashboard/${c.clubId}`); }}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{c.name}</span>
                            <span style={{ fontSize: 10, color: '#666' }}>#{c.clubId}</span>
                          </button>
                        ))}
                      </>
                    )}
                    <div className="dropdown-section-title">Club</div>
                    <button onClick={() => { setShowMenu(false); navigate(`/dashboard/${clubId}`); }}>Dashboard</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/edit`); }}>Manage Club</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/statement`); }}>Club Statement</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/goals`); }}>Manage Goals</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/invite`); }}>Invite Your Mates</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/lotto`); }}>Lotto Syndicate</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/withdrawals`); }}>Club Withdrawals</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/fund`); }}>Fund Club Account</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/subscription`); }}>Club Subscription</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/pending-bets`); }}>Pending Club Bets</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/bookie`); }}>Personal GRSBet Account</button>
                    <button onClick={() => { setShowMenu(false); navigate(`/club/${clubId}/achievements`); }}>Achievements</button>
                    <div className="dropdown-section-title">Account</div>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/personal'); }}>Personal Details</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/security'); }}>Security Details</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/profile-image'); }}>Profile Image</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/notifications'); }}>Notification Settings</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/subscriptions'); }}>Manage Subscriptions</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/bookie-accounts'); }}>Bookie Accounts</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/refer'); }}>Refer Your Mates</button>
                    <button onClick={() => { setShowMenu(false); navigate('/register/club'); }}>Start a Club</button>
                    <button onClick={() => { setShowMenu(false); navigate('/settings/leave-club'); }}>Leave a Club</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="stats-bar-wrapper">
          <div className="stats-bar">
            <div className="stat">
              <div className="stat-label">Balance</div>
              <div className="stat-value">{formatCurrencyShort(totalBalance)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">ROI</div>
              <div className={`stat-value ${stats.roi >= 0 ? 'positive' : ''}`}>
                {stats.roi.toFixed(0)}%
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Wagered</div>
              <div className="stat-value">{formatCurrencyShort(stats.totalWagered.cents)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Returned</div>
              <div className="stat-value">{formatCurrencyShort(stats.totalWon.cents)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Punting Now</div>
              <div className="stat-value name">
                {puntingSchedule?.allPuntersNow?.length > 0 ? (
                  <>
                    {puntingSchedule.allPuntersNow[0].name}
                    {puntingSchedule.allPuntersNow.length > 1 && (
                      <span className="punters-count-wrapper">
                        <span className="punters-count-badge">
                          (+{puntingSchedule.allPuntersNow.length - 1})
                        </span>
                        <div className="punters-popup">
                          {puntingSchedule.allPuntersNow.slice(1).map((p, idx) => (
                            <div key={p.userId || idx} className="punters-popup-item">{p.name}</div>
                          ))}
                        </div>
                      </span>
                    )}
                  </>
                ) : '-'}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Next Up</div>
              <div className="stat-value name">{puntingSchedule?.nextUp?.name || '-'}</div>
            </div>
            <div className="stat">
              <div className="stat-label">{club.isPaused ? 'Status' : 'Time Left'}</div>
              {club.isPaused ? (
                <div className="stat-value paused"><i className="fas fa-pause-circle"></i> Paused</div>
              ) : puntingSchedule?.periodEnd ? (
                <div className="stat-countdown">
                  <div className="stat-countdown-segment">
                    <span className="stat-countdown-value">{countdown.days}</span>
                    <span className="stat-countdown-label">D</span>
                  </div>
                  <span className="stat-countdown-separator">:</span>
                  <div className="stat-countdown-segment">
                    <span className="stat-countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
                    <span className="stat-countdown-label">H</span>
                  </div>
                  <span className="stat-countdown-separator">:</span>
                  <div className="stat-countdown-segment">
                    <span className="stat-countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
                    <span className="stat-countdown-label">M</span>
                  </div>
                  <span className="stat-countdown-separator">:</span>
                  <div className="stat-countdown-segment">
                    <span className="stat-countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
                    <span className="stat-countdown-label">S</span>
                  </div>
                </div>
              ) : (
                <div className="stat-value">-</div>
              )}
            </div>
          </div>
        </div>

        <div className="club-goal">
          <div className="club-goal-content">
            No club goal set. <a>Click here to add one now.</a>
          </div>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="nav-bar">
        <div className="nav-bar-wrapper">
          <div className="nav-left">
            <div className="nav-action club-selector-wrapper" onClick={() => setShowClubSelector(!showClubSelector)} style={{ marginRight: '8px', color: '#fff' }}>
              <span className="nav-action-icon" style={{ color: '#fff' }}><i className="fas fa-right-left"></i></span>
              {showClubSelector && clubs.length > 0 && (
                <div className="club-selector-dropdown" style={{ left: 0, right: 'auto' }}>
                  {clubs.map((c) => (
                    <div
                      key={c.id}
                      className={`club-selector-item ${c.id === parseInt(clubId) ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSwitchClub(c.id);
                      }}
                    >
                      <span className="club-selector-name">{c.name}</span>
                      {c.id === parseInt(clubId) && <i className="fas fa-check"></i>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <h1 className="club-title">{club.name}</h1>
          </div>
          <div className="nav-actions">
            <div className={`nav-action ${dashboardMode === 'lotto' ? 'active' : ''}`} onClick={() => setDashboardMode(dashboardMode === 'lotto' ? 'betting' : 'lotto')}>
              <span className="nav-action-icon"><i className="fas fa-ticket"></i></span>
              <span className="nav-action-label">Lotto</span>
            </div>
            <div className={`nav-action ${dashboardMode === 'shares' ? 'active' : ''}`} onClick={() => setDashboardMode(dashboardMode === 'shares' ? 'betting' : 'shares')}>
              <span className="nav-action-icon"><i className="fas fa-chart-line"></i></span>
              <span className="nav-action-label">Shares</span>
            </div>
            <div className={`nav-action ${dashboardMode === 'crypto' ? 'active' : ''}`} onClick={() => setDashboardMode(dashboardMode === 'crypto' ? 'betting' : 'crypto')}>
              <span className="nav-action-icon"><i className="fab fa-bitcoin"></i></span>
              <span className="nav-action-label">Crypto</span>
            </div>
            <div style={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.2)', alignSelf: 'stretch', margin: '0 4px' }} />
            <div className="nav-action" onClick={() => navigate(`/club/${clubId}/edit`)}>
              <span className="nav-action-icon"><i className="fas fa-cog"></i></span>
              <span className="nav-action-label">Manage Club</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Container */}
      <div className="container">
        {/* Main Content */}
        <div className="main-content">
          {activeView === 'dashboard' ? (
            <>
              {/* Leaderboard (betting mode only) */}
              {dashboardMode === 'betting' && <div className="leaderboard-card">
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>PUNTER</th>
                      <th>TRENDS</th>
                      <th>ROI</th>
                      <th>WAGERED</th>
                      <th>WON</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, index) => (
                      <tr key={member.userId}>
                        <td><span className="rank-number">{index + 1}</span></td>
                        <td>
                          <div className="punter-cell">
                            <div className="punter-avatar">
                              {member.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="punter-name">{member.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="trends">
                            {member.trends && member.trends.length > 0 ? (
                              member.trends.map((trend, i) => (
                                <div key={i} className={`trend-item ${trend.won ? 'win' : 'loss'}`}>
                                  <i className={`fas fa-${trend.won ? 'trophy' : 'heart-broken'}`}></i>
                                  <div className="trend-popover">
                                    <div className="trend-popover-header">
                                      <span className={`trend-result ${trend.won ? 'win' : 'loss'}`}>
                                        {trend.won ? 'WIN' : 'LOSS'}
                                      </span>
                                      <div className="trend-selection">{trend.selection || 'N/A'}</div>
                                    </div>
                                    <div className="trend-popover-body">
                                      <div className="trend-detail">
                                        <span className="trend-label">Event:</span>
                                        <span className="trend-value">{trend.event || 'N/A'}</span>
                                      </div>
                                      <div className="trend-detail">
                                        <span className="trend-label">Bet:</span>
                                        <span className="trend-value">${trend.amount?.toFixed(2)}</span>
                                      </div>
                                      <div className="trend-detail">
                                        <span className="trend-label">Odds:</span>
                                        <span className="trend-value">{trend.odds?.toFixed(2)}</span>
                                      </div>
                                      <div className="trend-detail">
                                        <span className="trend-label">Payout:</span>
                                        <span className="trend-value">${trend.payout?.toFixed(2) || '0.00'}</span>
                                      </div>
                                      <div className="trend-detail">
                                        <span className="trend-label">Date:</span>
                                        <span className="trend-value">{trend.date ? new Date(trend.date).toLocaleDateString() : 'N/A'}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <span className="trend-icon">
                                <i className="fas fa-minus"></i>
                              </span>
                            )}
                          </div>
                        </td>
                        <td><span className={`roi-value ${(member.roi || 0) >= 0 ? 'positive' : 'negative'}`}>{(member.roi || 0).toFixed(0)}%</span></td>
                        <td><span className="money-value">{formatCurrencyShort(member.wagered || 0)}</span></td>
                        <td><span className="money-value">{formatCurrencyShort(member.won || 0)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}

              {/* Lotto Syndicate (lotto mode only) */}
              {dashboardMode === 'lotto' && (
                <ClubLotto clubId={clubId} embedded={true} />
              )}

              {/* Shares - Coming Soon */}
              {dashboardMode === 'shares' && (
                <div style={{ backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem', border: '1px solid var(--border, #334155)', padding: '3rem', textAlign: 'center' }}>
                  <i className="fas fa-chart-line" style={{ fontSize: '3rem', color: 'var(--primary, #f0c040)', marginBottom: '1rem', display: 'block' }}></i>
                  <h3 style={{ color: 'var(--text-primary, #fff)', margin: '0 0 0.5rem' }}>Shares Syndicate</h3>
                  <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: 0 }}>Coming soon. Pool funds with your club to invest in shares together.</p>
                </div>
              )}

              {/* Crypto Syndicate */}
              {dashboardMode === 'crypto' && (
                <ClubCrypto clubId={clubId} embedded={true} />
              )}

              {/* Wall Feed (always visible) */}
              <WallFeed clubId={parseInt(clubId)} />
            </>
          ) : (
            /* Settings View */
            <div className="settings-panel">
              {settingsError && <div className="settings-alert settings-alert-error"><i className="fas fa-exclamation-circle"></i> {settingsError}</div>}
              {settingsSuccess && <div className="settings-alert settings-alert-success"><i className="fas fa-check-circle"></i> {settingsSuccess}</div>}

              {/* Sub-menu navigation */}
              <div className="settings-submenu">
                <button
                  type="button"
                  className={`settings-submenu-item ${settingsTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setSettingsTab('settings')}
                >
                  <i className="fas fa-cog"></i>
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  className={`settings-submenu-item ${settingsTab === 'members' ? 'active' : ''}`}
                  onClick={() => setSettingsTab('members')}
                >
                  <i className="fas fa-users"></i>
                  <span>Members</span>
                </button>
                <button
                  type="button"
                  className={`settings-submenu-item ${settingsTab === 'statement' ? 'active' : ''}`}
                  onClick={() => setSettingsTab('statement')}
                >
                  <i className="fas fa-file-invoice-dollar"></i>
                  <span>Club Statement</span>
                </button>
              </div>

              {/* Settings Tab */}
              {settingsTab === 'settings' && (
                <>
                  {!settingsData ? (
                    <div className="settings-loading"><i className="fas fa-spinner fa-spin"></i> Loading settings...</div>
                  ) : (
                    <form className="settings-simple-form" onSubmit={handleSettingsSubmit}>
                      <div className="settings-header">
                        <h3>Club Settings</h3>
                        <p>Update your club settings, betting frequencies, rollover days, and notifications.</p>
                      </div>

                      <div className="settings-grid">
                        <div className="settings-form-group full-width">
                          <label>Club Name</label>
                          <input type="text" name="name" value={settingsFormData.name || ''} onChange={handleSettingsInputChange} />
                        </div>
                        <div className="settings-row">
                          <div className="settings-form-group">
                            <label>Contribution</label>
                            <div className="settings-input-group">
                              <span className="settings-input-prefix">$</span>
                              <input type="number" name="contributionAmount" value={settingsFormData.contributionAmount || ''} onChange={handleSettingsInputChange} />
                              <select name="contributionFrequency" value={settingsFormData.contributionFrequency || 'weekly'} onChange={handleSettingsInputChange}>
                                {settingsOptions.contributionFrequencyOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="settings-form-group">
                            <label>Bet Frequency</label>
                            <select name="betFrequency" value={settingsFormData.betFrequency || 'weekly'} onChange={handleSettingsInputChange}>
                              {settingsOptions.frequencyOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="settings-row">
                          <div className="settings-form-group">
                            <label>Bet Amount</label>
                            <input type="number" name="betPercentage" value={settingsFormData.betPercentage || ''} onChange={handleSettingsInputChange} />
                          </div>
                          <div className="settings-form-group">
                            <label>Bet Type</label>
                            <select name="betAmountType" value={settingsFormData.betAmountType || 'percent'} onChange={handleSettingsInputChange}>
                              {settingsOptions.amountTypeOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="settings-row">
                          <div className="settings-form-group">
                            <label>Rollover Day</label>
                            <select name="dayOfTheWeek" value={settingsFormData.dayOfTheWeek ?? 1} onChange={handleSettingsInputChange}>
                              {settingsOptions.daysOfWeek.map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="settings-form-group">
                            <label>Punters Per Period</label>
                            <select name="puntersPerPeriod" value={settingsFormData.puntersPerPeriod || 1} onChange={handleSettingsInputChange}>
                              <option value="all">All</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="settings-row">
                          <div className="settings-form-group">
                            <label>Pass Captain Rights</label>
                            <select name="captainUserId" value={settingsFormData.captain?.userId || ''} onChange={handleSettingsInputChange}>
                              {eligibleCaptains.map(member => (
                                <option key={member.userId} value={member.userId}>{member.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="settings-form-group">
                            <label>Keep Punting ROI</label>
                            <input type="number" name="minimumRoi" value={settingsFormData.minimumRoi || 200} onChange={handleSettingsInputChange} />
                          </div>
                        </div>
                      </div>

                      <div className="settings-checkboxes">
                        <label className="settings-checkbox">
                          <input type="checkbox" name="allowWallPosts" checked={settingsFormData.allowWallPosts ?? true} onChange={handleSettingsInputChange} />
                          <span>Wall Sledges</span>
                        </label>
                        <label className="settings-checkbox">
                          <input type="checkbox" name="smsNotifications" checked={settingsFormData.smsNotifications ?? false} onChange={handleSettingsInputChange} />
                          <span>SMS Notifications (Charges Apply)</span>
                        </label>
                      </div>

                      <div className="settings-actions">
                        <button type="submit" className="settings-btn-save" disabled={settingsSaving}>
                          {settingsSaving ? 'Saving...' : 'Save'}
                        </button>
                      </div>

                      <div className="settings-tools-section">
                        <h3>Management Tools</h3>
                        <p>Pause/unpause your club, reload betting account, reset ROI, and reset contributions.</p>
                        <div className="settings-tools-buttons">
                          <button type="button" className={`settings-tool-btn ${settingsFormData.isPaused ? 'success' : 'warning'}`} onClick={handlePauseToggle}>
                            {settingsFormData.isPaused ? 'Unpause Club' : 'Pause Club'}
                          </button>
                          <button type="button" className={`settings-tool-btn primary ${reloadingBetting ? 'loading' : ''}`} disabled={reloadingBetting} onClick={async () => {
                            setReloadingBetting(true);
                            setReloadBettingResult(null);
                            try {
                              const data = await clubAPI.reloadBettingAccount(clubId);
                              setReloadBettingResult(data);
                              if (data.success) loadDashboard();
                            } catch (err) {
                              setReloadBettingResult({ success: false, error: err.response?.data?.error || 'Reload failed' });
                            } finally {
                              setReloadingBetting(false);
                            }
                          }}>
                            {reloadingBetting ? <><i className="fas fa-spinner fa-spin"></i> Reloading...</> : 'Reload Betting Account'}
                          </button>
                          {reloadBettingResult && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: reloadBettingResult.success ? '#10b981' : '#ef4444' }}>
                              {reloadBettingResult.success ? reloadBettingResult.message : reloadBettingResult.error}
                            </div>
                          )}
                          <button type="button" className="settings-tool-btn danger" disabled>Reset Leaderboard</button>
                          <button type="button" className="settings-tool-btn danger" disabled>Reset Contributions</button>
                        </div>
                      </div>
                    </form>
                  )}
                </>
              )}

              {/* Members Tab */}
              {settingsTab === 'members' && (
                <div className="settings-tab-content">
                  <div className="settings-header">
                    <h3>Manage Members</h3>
                  </div>
                  <div className="members-manage-list">
                    {members.map((member) => (
                      <div key={member.userId} className={`member-manage-item ${member.status === 'suspended' ? 'suspended' : ''}`}>
                        <div className="member-manage-avatar">
                          {member.name.split(' ').map(n => n.charAt(0).toUpperCase()).join('').slice(0, 2)}
                        </div>
                        <div className="member-manage-info">
                          <div className="member-manage-name">{member.name}</div>
                          <div className="member-manage-email">{member.email}</div>
                        </div>
                        <div className="member-manage-controls">
                          <select
                            className={`member-status-select ${member.status}`}
                            value={member.status}
                            onChange={(e) => handleMemberStatusChange(member.userId, e.target.value)}
                            disabled={member.isCaptain}
                          >
                            <option value="active">ACTIVE</option>
                            <option value="suspended">SUSPENDED</option>
                          </select>
                          <select
                            className={`member-betting-select ${member.isBetting ? 'betting' : 'not-betting'}`}
                            value={member.isBetting ? 'betting' : 'notBetting'}
                            onChange={(e) => handleMemberBettingChange(member.userId, e.target.value === 'betting')}
                            disabled={member.status !== 'active'}
                          >
                            <option value="betting">BETTING NOW</option>
                            <option value="notBetting">NOT BETTING</option>
                          </select>
                        </div>
                        <div className="member-manage-actions">
                          {!member.isCaptain && (
                            <button
                              type="button"
                              className="member-action-btn eject"
                              onClick={() => handleEjectMember(member.userId, member.name)}
                            >
                              Eject
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Statement Tab */}
              {settingsTab === 'statement' && (
                <div className="settings-tab-content">
                  {/* Club Details */}
                  <div className="statement-section">
                    <h3><i className="fas fa-info-circle"></i> Club Details</h3>
                    <div className="statement-details-grid">
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Betting Balance</span>
                        <span className="statement-detail-value">{formatCurrencyShort(accounts?.betting?.balance || 0)}</span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Kitty Balance</span>
                        <span className="statement-detail-value">{formatCurrencyShort(accounts?.kitty?.balance || 0)}</span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Contribution Frequency</span>
                        <span className="statement-detail-value" style={{ textTransform: 'capitalize' }}>{club.contributionFrequency || 'Weekly'}</span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Contribution Amount</span>
                        <span className="statement-detail-value">${club.contributionAmount || 0}</span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Bet Frequency</span>
                        <span className="statement-detail-value" style={{ textTransform: 'capitalize' }}>{club.betFrequency || 'Weekly'}</span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Bet Amount</span>
                        <span className="statement-detail-value">
                          {club.betAmountType === 'percent'
                            ? `${club.betPercentage}% of kitty`
                            : club.betPercentage
                              ? `$${club.betPercentage}`
                              : 'Unknown'}
                        </span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Next Betting Period</span>
                        <span className="statement-detail-value">
                          {puntingSchedule?.periodEnd
                            ? new Date(puntingSchedule.periodEnd).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="statement-detail-row">
                        <span className="statement-detail-label">Captain</span>
                        <span className="statement-detail-value">{club.captain?.name || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Members Contributions */}
                  <div className="statement-section">
                    <h3><i className="fas fa-users"></i> Members</h3>
                    <p className="statement-section-note">You can click on a member for more information and click on the headings to sort</p>
                    <div className="statement-recalculate">
                      <span>Contributions look incorrect? </span>
                      <button type="button" className="link-btn">recalculate now</button>
                    </div>
                    <table className="statement-members-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Contributions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((member) => (
                          <tr key={member.userId}>
                            <td>
                              <div className="statement-member-cell">
                                <span className="statement-contribution-badge">${(member.contributions || 0).toFixed(2)}</span>
                                <span>{member.name}</span>
                              </div>
                            </td>
                            <td>${(member.contributions || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="statement-download">
                      <button type="button" className="btn-download">
                        <i className="fas fa-download"></i> Download
                      </button>
                    </div>
                  </div>

                  {/* Withdrawals */}
                  <div className="statement-section">
                    <h3><i className="fas fa-money-bill-wave"></i> Withdrawals</h3>
                    <p className="statement-no-data">No withdrawals made.</p>
                  </div>

                  {/* Transactions */}
                  <div className="statement-section">
                    <h3><i className="fas fa-right-left"></i> Transactions</h3>
                    <div className="statement-transaction-tabs">
                      <button
                        type="button"
                        className={`statement-tab-btn ${transactionTab === 'kitty' ? 'active' : ''}`}
                        onClick={() => setTransactionTab('kitty')}
                      >
                        Kitty Account
                      </button>
                      <button
                        type="button"
                        className={`statement-tab-btn ${transactionTab === 'betting' ? 'active' : ''}`}
                        onClick={() => setTransactionTab('betting')}
                      >
                        Betting Account
                      </button>
                    </div>
                    <div className="statement-transactions-content">
                      {transactionTab === 'kitty' && (
                        <>
                          {accounts?.kitty?.transactions && accounts.kitty.transactions.length > 0 ? (
                            <table className="transactions-table">
                              <thead>
                                <tr>
                                  <th>Time</th>
                                  <th>Description</th>
                                  <th className="text-right">Amount</th>
                                  <th className="text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {renderTransactionsWithDateHeaders(accounts.kitty.transactions)}
                              </tbody>
                            </table>
                          ) : (
                            <p className="statement-no-data">No kitty transactions to display.</p>
                          )}
                        </>
                      )}
                      {transactionTab === 'betting' && (
                        <>
                          {accounts?.betting?.transactions && accounts.betting.transactions.length > 0 ? (
                            <table className="transactions-table">
                              <thead>
                                <tr>
                                  <th>Time</th>
                                  <th>Description</th>
                                  <th className="text-right">Amount</th>
                                  <th className="text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {renderTransactionsWithDateHeaders(accounts.betting.transactions)}
                              </tbody>
                            </table>
                          ) : (
                            <p className="statement-no-data">No betting transactions to display.</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="sidebar">
          {hasStarted ? (
            <button className="bet-now-btn" onClick={openBetModal}>BET NOW</button>
          ) : (
            <button className="bet-now-btn start-now-btn" onClick={handleStartClub} disabled={startingClub}>
              {startingClub ? 'STARTING...' : 'START NOW'}
            </button>
          )}

          <div className="sidebar-card club-info-card">
            <div className="sidebar-header">
              <span>CLUB INFO</span>
              <span className="settings-icon" onClick={handleManageClubClick} style={{ cursor: 'pointer' }}><i className="fas fa-cog"></i></span>
            </div>
            <div className="club-info-grid">
              <div className="club-info-row">
                <span className="club-info-label">Captain</span>
                <span className="club-info-value">{club.captain?.name || 'Unknown'}</span>
              </div>
              <div className="club-info-row">
                <span className="club-info-label">Members</span>
                <span className="club-info-value">{memberCount} <span className="invite-link">(+ invite)</span></span>
              </div>
              <div className="club-info-row">
                <span className="club-info-label">Betting {club.betFrequency}</span>
                <span className="club-info-value">{club.betAmountType === 'percent' ? `${club.betPercentage}%` : `$${club.betPercentage}`}</span>
              </div>
              {club.contributionAmount && (
                <div className="club-info-row">
                  <span className="club-info-label">Contributions {club.contributionFrequency}</span>
                  <span className="club-info-value">${club.contributionAmount}</span>
                </div>
              )}
            </div>
          </div>

          {/* Members Card */}
          <div className="sidebar-card">
            <div className="sidebar-header">
              <span>MEMBERS</span>
            </div>
            <div className="top-clubs-list">
              {members.map((member, index) => (
                <div key={member.userId} className="top-club-item">
                  <span className="club-rank">{index + 1}</span>
                  <span className="club-name">{member.name}</span>
                  <span className="club-roi positive">{member.status}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Bet Now Wallet Selector Modal */}
      {showBetModal && (
        <div className="bet-modal-overlay" onClick={() => !betLoading && setShowBetModal(false)}>
          <div className="bet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bet-modal-header">
              <h2>Choose Your Account</h2>
              <button className="bet-modal-close" onClick={() => !betLoading && setShowBetModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <p className="bet-modal-subtitle">Select which account you'd like to bet with</p>
            <div className="bet-modal-options">
              <button
                className="bet-modal-option"
                onClick={() => handleBetNow('personal')}
                disabled={betLoading}
              >
                <div className="bet-modal-option-icon">
                  <i className="fas fa-user"></i>
                </div>
                <div className="bet-modal-option-info">
                  <div className="bet-modal-option-title">Personal Account</div>
                  <div className="bet-modal-option-desc">Bet using your personal betting wallet</div>
                </div>
                <i className="fas fa-chevron-right bet-modal-option-arrow"></i>
              </button>
              <button
                className="bet-modal-option"
                onClick={() => handleBetNow('club')}
                disabled={betLoading}
              >
                <div className="bet-modal-option-icon club">
                  <i className="fas fa-users"></i>
                </div>
                <div className="bet-modal-option-info">
                  <div className="bet-modal-option-title">Club Betting Account</div>
                  <div className="bet-modal-option-desc">Bet using {club.name}'s betting wallet</div>
                  {balanceLoading ? (
                    <div className="bet-modal-option-balance loading">Loading balance...</div>
                  ) : bookieBalances ? (
                    <div className="bet-modal-option-balance">${bookieBalances.betting.dollars.toFixed(2)}</div>
                  ) : null}
                </div>
                <i className="fas fa-chevron-right bet-modal-option-arrow"></i>
              </button>
            </div>
            {betLoading && (
              <div className="bet-modal-loading">
                <i className="fas fa-spinner fa-spin"></i> Opening betting site...
              </div>
            )}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default ClubDashboard;
